import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import classNames from 'classnames';
import { window } from 'browser-monads';
import { GoogleDisclaimer } from './googleDisclaimer';
import { IOption, mockOptions } from './googleAddressApi';
import { enrollmentContext } from '../../../../hoc/context/enrollment/enrollment-context';
import Tooltip from '../../../../hoc/components/tooltip';
import { TextBox } from '../../../../hoc/components/inputs';
import { InputLabel } from '../../../../hoc/components/inputs/common-components/input-label';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import { RichText } from '../../../../hoc/components';
import { UseFormMethods } from 'react-hook-form';
import useMockConfiguration from '../../../../../hooks/useMockConfiguration';
import context from '../../../../hoc/context/product/context';

interface AddressQuickFillComponentProps {
	name: string;
	label?: string;
	classname: string;
	required: boolean;
	errors: UseFormMethods['errors'];
	register: UseFormMethods['register'];
	setValue: UseFormMethods['setValue'];
	formState: UseFormMethods['formState'];
	suffix?: string;
	onFocus: Function;
	onChange: Function;
	onUpdate?: Function;
}

const updateCityField = (
	addressComponent: { types: string[]; long_name: string },
	completeAddress: { city: string }
) => {
	if (
		addressComponent.types.includes('locality') ||
		addressComponent.types.includes('sublocality') ||
		addressComponent.types.includes('sublocality_level_1') ||
		addressComponent.types.includes('sublocality_level_2') ||
		addressComponent.types.includes('sublocality_level_3') ||
		addressComponent.types.includes('sublocality_level_4')
	) {
		completeAddress.city = addressComponent.long_name;
	}
};

// Highlights in bold the value entered by the user
const formatPrediction = (enteredVal: string, prediction: string) => {
	const trimmedEnteredVal = enteredVal.trim().toLowerCase();
	const indexOfEnteredVal = prediction
		.toLocaleLowerCase()
		.indexOf(trimmedEnteredVal);
	let formattedPrediction = prediction;
	if (indexOfEnteredVal > -1) {
		formattedPrediction =
			prediction.substring(0, indexOfEnteredVal) +
			'<b>' +
			prediction.substr(indexOfEnteredVal, trimmedEnteredVal.length) +
			'</b>' +
			prediction.substring(
				indexOfEnteredVal + trimmedEnteredVal.length,
				prediction.length
			);
	}
	return formattedPrediction;
};

const processPlacesResponse = (
	placeResult: google.maps.places.PlaceResult | null
) => {
	const addressComponents = placeResult?.address_components;
	return addressComponents?.reduce(
		(
			completeAddress: {
				mail: string;
				city: string;
				state: string;
				zip: string;
			},
			addressComponent: {
				short_name: string;
				long_name: string;
				types: string[];
			}
		) => {
			if (addressComponent.types.includes('postal_code')) {
				completeAddress.zip = addressComponent.long_name;
			}
			if (addressComponent.types.includes('administrative_area_level_1')) {
				completeAddress.state = addressComponent.short_name;
			}
			if (
				addressComponent.types.includes('country') &&
				addressComponent.short_name !== 'US'
			) {
				completeAddress.state = addressComponent.short_name;
			}
			if (addressComponent.types.includes('street_number')) {
				completeAddress.mail =
					addressComponent.short_name + ' ' + completeAddress.mail;
			}
			if (addressComponent.types.includes('route')) {
				completeAddress.mail += addressComponent.long_name;
			}
			updateCityField(addressComponent, completeAddress);
			return completeAddress;
		},
		{ mail: '', city: '', state: '', zip: '' }
	);
};

export const AddressQuickFill = ({
	name,
	label,
	classname,
	errors,
	formState,
	register,
	required,
	setValue,
	suffix,
	onChange,
	onUpdate,
}: AddressQuickFillComponentProps) => {
	const enrollmentCtxt = useContext(enrollmentContext);
	const ctxt = useContext(context);
	const statesList = ctxt.json.getField('state' + suffix)?.options;
	const mockConfig = useMockConfiguration();
	const [showOptions, setShowOptions] = useState(false);
	const [enteredVal, setEnteredVal] = useState('');
	const [isMinQuery, setIsMinQuery] = useState(false);
	const [mailValue, setMailValue] = useState(
		enrollmentCtxt.getFormField('mail' + suffix)?.value
	);
	const googlePlacesQuickFill = useRef<HTMLDivElement>(null);
	const maxLength = 50;
	const {
		placePredictions,
		getPlacePredictions,
		autocompleteSessionToken,
		refreshSessionToken,
		placesService,
	} = usePlacesService({
		apiKey: process.env.GATSBY_GOOGLE_API_KEY as string,
		debounce: parseInt(process.env.GATSBY_GOOGLE_API_DEBOUNCE as string),
		sessionToken: true,
	});

	const placePredictionsList = [...placePredictions];
	const filteredPredictions = placePredictionsList?.filter((item) => {
		return item.terms?.some((term) =>
			statesList?.some((state: { value: string }) => state.value === term.value)
		);
	});

	const closeOptionsDropdown = useCallback(() => {
		if (googlePlacesQuickFill.current) {
			setShowOptions(false);
		}
	},[setShowOptions]);

	useEffect(() => {
		window.addEventListener('click', closeOptionsDropdown);
		return () => {
			window.removeEventListener('click', closeOptionsDropdown);
		};
	}, [closeOptionsDropdown]);


	useEffect(()=>{
		setShowOptions(
			filteredPredictions.length > 0 && isMinQuery && enteredVal.length > 0
		);
	},[filteredPredictions.length,isMinQuery,enteredVal.length,setShowOptions])

	const updateAddressValues = (option: IOption) => {
		if (option) {
			setMailValue(option.mail);
			setValue('mail' + suffix, option.mail, { shouldValidate: true });
			setValue('city' + suffix, option.city, { shouldValidate: true });
			setValue('state' + suffix, option.state, { shouldValidate: true });
			setValue('zip' + suffix, option.zip, { shouldValidate: true });
			if (onUpdate) {
				onUpdate();
			}
		}
	};

	const selectOption = (
		option: IOption | google.maps.places.AutocompletePrediction
	) => {
		if (option.place_id) {
			placesService?.getDetails(
				{
					placeId: option.place_id,
					sessionToken: autocompleteSessionToken,
				},
				(placeResult) => {
					const resultOption = processPlacesResponse(placeResult);
					updateAddressValues(resultOption!);
					refreshSessionToken();
				}
			);
		} else if (mockConfig.isMockAllowed) {
			updateAddressValues(option);
		}
		setShowOptions(false);
	};

	const lookupAddress = (mailAddressLookup: string) => {
		setEnteredVal(mailAddressLookup);
		setIsMinQuery(
			mailAddressLookup.length >=
				parseInt(
					process.env.GATSBY_GOOGLE_API_REQUEST_MINIMUM_QUERY_LENGTH as string
				)
		);
		const country = ['us', 'pr', 'vi', 'gu', 'mp'];
		getPlacePredictions({
			input: mailAddressLookup,
			componentRestrictions: { country },
		});
	};
	// FIXME Refactor this component to reuse the standard textbox + label and review the use of tailwind
	return (
		<div
			className={classNames('quickfill relative', {
				[`${classname}`]: !!classname,
				'no-error': !errors[name],
			})}>
			<div className="flex items-center">
				<InputLabel
					label={label}
					required={required}
					errors={errors}
					name={name}
					className={'text-font'}
				/>
				<Tooltip
					icon="?"
					modal={true}
					fieldname={name}
					className="ml-2 w-4 h-4 inline-flex justify-center items-center rounded-full text-xs text-font-contrast bg-brand">
					<GoogleDisclaimer className="leading-tight" />
				</Tooltip>
			</div>
			<TextBox
				name={name}
				setValue={setValue}
				value={mailValue}
				required={required}
				onChange={(value: string) => {
					lookupAddress(value);
					onChange(value);
				}}
				errors={errors}
				formState={formState}
				register={register}
				maxLength={maxLength}
			/>
			{showOptions && (
				<div
					id="googlePlacesQuickFillOptions"
					data-testid="googlePlacesQuickFillOptions"
					ref={googlePlacesQuickFill}
					className="after:bg-[url('https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3.png')] after:bg-no-repeat after:px-px after:py-px after:bg-[length:120px_14px] after:block after:box-border after:bg-right after:h-[1.125rem] absolute bg-white w-full z-10 mt-1 shadow-xl border-white">
					<ul className="mt-4">
						{(mockConfig.isMockAllowed ? mockOptions : filteredPredictions).map(
							(option, i) => {
								return (
									<li
										key={i}
										className="text-font px-5 py-1 focus:bg-neutral hover:bg-neutral cursor-pointer"
										tabIndex={0}
										onKeyUp={(event) => {
											if (event.key === 'Enter') {
												selectOption(option);
											}
										}}
										onClick={() => {
											selectOption(option);
										}}>
										<RichText
											html={formatPrediction(enteredVal, option.description)}
										/>
									</li>
								);
							}
						)}
					</ul>
				</div>
			)}
		</div>
	);
};
